/**
 * ClassificationPreview.tsx - Full Company Classification Page
 *
 * Complete port of Section 02 with 60+ fields, sector-specific thresholds,
 * and comprehensive classification display.
 */

import { useState, useEffect, ReactElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  TrendingUp,
  Activity,
  Users,
  AlertCircle,
  Loader2,
  RefreshCw,
  Shield,
  Scale,
  PieChart,
  Award,
  DollarSign,
  BarChart3,
  Target,
  Zap,
  Heart,
  Briefcase,
} from 'lucide-react';
import {
  stocksAPI,
  CompanyClassificationFullResponse,
} from '../../services/api';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const ClassificationTag = ({
  label,
  variant = 'default',
}: {
  label: string;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
}) => {
  const variants = {
    default: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    primary: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    success: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    danger: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    info: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${variants[variant]}`}>
      {label}
    </span>
  );
};

const MetricRow = ({
  label,
  value,
  suffix = '',
  classification,
  variant,
}: {
  label: string;
  value: number | string | null | undefined;
  suffix?: string;
  classification?: string;
  variant?: 'success' | 'warning' | 'danger';
}) => {
  const getValueColor = () => {
    if (!variant) return 'text-gray-900 dark:text-white';
    if (variant === 'success') return 'text-green-600 dark:text-green-400';
    if (variant === 'warning') return 'text-yellow-600 dark:text-yellow-400';
    if (variant === 'danger') return 'text-red-600 dark:text-red-400';
    return 'text-gray-900 dark:text-white';
  };

  const formatValue = (val: number | string | null | undefined): string => {
    if (val === null || val === undefined) return 'N/A';
    if (typeof val === 'number') {
      if (Math.abs(val) >= 1e12) return `${(val / 1e12).toFixed(2)}T`;
      if (Math.abs(val) >= 1e9) return `${(val / 1e9).toFixed(2)}B`;
      if (Math.abs(val) >= 1e6) return `${(val / 1e6).toFixed(2)}M`;
      return val.toFixed(2);
    }
    return val;
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-gray-600 dark:text-gray-400 text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`font-medium ${getValueColor()}`}>
          {formatValue(value)}{suffix}
        </span>
        {classification && (
          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
            {classification}
          </span>
        )}
      </div>
    </div>
  );
};

const ScoreBar = ({
  label,
  score,
  maxScore = 10,
}: {
  label: string;
  score: number;
  maxScore?: number;
}) => {
  const percentage = Math.min(100, (score / maxScore) * 100);
  const getColor = () => {
    if (percentage >= 70) return 'bg-green-500';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="font-medium text-gray-900 dark:text-white">{score.toFixed(1)}</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${getColor()}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const GradeCard = ({
  label,
  grade,
  description,
}: {
  label: string;
  grade: string;
  description?: string;
}) => {
  const getGradeColor = (g: string) => {
    const firstChar = g.charAt(0).toUpperCase();
    if (firstChar === 'A' || g === '9' || g === '8' || g === '7') return 'bg-green-500 text-white';
    if (firstChar === 'B' || g === '6' || g === '5') return 'bg-blue-500 text-white';
    if (firstChar === 'C' || g === '4' || g === '3') return 'bg-yellow-500 text-white';
    if (firstChar === 'D' || g === '2' || g === '1') return 'bg-orange-500 text-white';
    return 'bg-red-500 text-white';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{label}</div>
      <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center text-xl font-bold ${getGradeColor(grade)}`}>
        {grade}
      </div>
      {description && (
        <div className="text-xs text-gray-600 dark:text-gray-300 mt-2">{description}</div>
      )}
    </div>
  );
};

const SectionCard = ({
  title,
  icon: Icon,
  children,
  className = '',
  badge,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  className?: string;
  badge?: string;
}) => (
  <div className={`bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
        <Icon className="w-5 h-5 text-gray-500" />
        {title}
      </h2>
      {badge && (
        <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
          {badge}
        </span>
      )}
    </div>
    {children}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ClassificationPreview(): ReactElement {
  const { symbol } = useParams<{ symbol: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<CompanyClassificationFullResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClassification = async () => {
    if (!symbol) return;

    setLoading(true);
    setError(null);

    try {
      const response = await stocksAPI.getCompanyClassificationFull(symbol.toUpperCase());
      setData(response.data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load classification';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassification();
  }, [symbol]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading full classification for {symbol?.toUpperCase()}...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
              <AlertCircle className="w-6 h-6" />
              <div>
                <h3 className="font-semibold">Error Loading Classification</h3>
                <p className="text-sm mt-1">{error || 'Unknown error'}</p>
              </div>
            </div>
            <button
              onClick={fetchClassification}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-800 rounded-lg text-red-700 dark:text-red-300 hover:bg-red-200"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper for variant based on value
  //const getValueVariant = (condition: boolean | null): 'success' | 'warning' | 'danger' | undefined => {
  //  if (condition === null) return undefined;
  //  return condition ? 'success' : 'danger';
  //};

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-green-700 dark:text-green-400">
              Full Classification (60+ fields)
            </span>
            <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded text-blue-700 dark:text-blue-400">
              {data.sector_classification_approach} Approach
            </span>
            <button
              onClick={fetchClassification}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Company Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {data.symbol}
                </h1>
                <span className="text-lg text-gray-500 dark:text-gray-400">
                  {data.company_name !== data.symbol ? data.company_name : ''}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                <Building2 className="w-4 h-4" />
                <span>{data.gics_sector}</span>
                <span className="text-gray-300">|</span>
                <span>{data.industry}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <ClassificationTag label={data.market_cap_bucket} variant="primary" />
                <ClassificationTag label={data.primary_style} variant="info" />
                <ClassificationTag label={data.health_grade} variant={data.health_grade === 'A' ? 'success' : data.health_grade === 'B' ? 'primary' : data.health_grade === 'C' ? 'warning' : 'danger'} />
                <ClassificationTag label={data.quality_rank} variant={data.quality_rank.includes('Top') ? 'success' : 'default'} />
                {data.investment_styles.map((style) => (
                  <ClassificationTag key={style} label={style} />
                ))}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {data.market_cap_formatted}
              </div>
              <div className="text-sm text-gray-500">Market Cap</div>
            </div>
          </div>
        </div>

        {/* Scores Overview */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-gray-500" />
            Investment Scores Overview
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <ScoreBar label="Growth" score={data.growth_score} />
            <ScoreBar label="Value" score={data.value_score} />
            <ScoreBar label="Quality" score={data.quality_score} />
            <ScoreBar label="Financial" score={data.financial_score} />
            <ScoreBar label="Defensive" score={data.defensive_score} />
            <ScoreBar label="Moat" score={data.moat_strength_score} />
          </div>
        </div>

        {/* Key Grades */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <GradeCard label="Health Grade" grade={data.health_grade} description={data.overall_health} />
          <GradeCard label="Altman Z-Score" grade={data.altman_z_score?.toFixed(1) || 'N/A'} description={data.altman_z_zone} />
          <GradeCard label="Piotroski F-Score" grade={String(data.piotroski_f_score)} description="/9 points" />
          <GradeCard label="Beta" grade={data.estimated_beta.toFixed(2)} description={data.beta_classification} />
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">

          {/* Investment Style */}
          <SectionCard title="Investment Style" icon={PieChart}>
            <div className="flex flex-wrap gap-2 mb-4">
              <ClassificationTag label={`Primary: ${data.primary_style}`} variant="primary" />
              <ClassificationTag label={`Secondary: ${data.secondary_style}`} variant="info" />
            </div>
            <MetricRow label="All Styles" value={data.investment_styles.join(', ')} />
          </SectionCard>

          {/* Valuation Metrics */}
          <SectionCard title="Valuation Metrics" icon={DollarSign}>
            <div className="flex items-center gap-2 mb-4">
              <ClassificationTag
                label={data.valuation_tier}
                variant={data.valuation_tier.includes('Value') ? 'success' : data.valuation_tier === 'Expensive' ? 'danger' : 'warning'}
              />
              <ClassificationTag
                label={data.value_rating}
                variant={data.value_rating === 'Buy' ? 'success' : data.value_rating === 'Avoid' ? 'danger' : 'default'}
              />
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">{data.fair_value_gap}</div>
            <MetricRow label="P/E Ratio" value={data.pe_ratio_actual} suffix="x" classification={data.pe_bucket} />
            <MetricRow label="P/B Ratio" value={data.pb_ratio_actual} suffix="x" classification={data.pb_bucket} />
            <MetricRow label="P/S Ratio" value={data.ps_ratio_actual} suffix="x" />
            <MetricRow label="EV/EBITDA" value={data.ev_ebitda_actual} suffix="x" classification={data.ev_ebitda_bucket} />
            <MetricRow label="PEG Ratio" value={data.peg_ratio_actual} suffix="x" classification={data.peg_bucket} />
            <MetricRow label="FCF Yield" value={data.fcf_yield} suffix="%" />
            <MetricRow label="Dividend Yield" value={data.dividend_yield} suffix="%" />
            <MetricRow label="Sector Relative" value={data.sector_relative_valuation} />
          </SectionCard>

          {/* Profitability */}
          <SectionCard title="Profitability Metrics" icon={Award} badge={data.quality_rank}>
            <MetricRow label="ROE" value={data.roe} suffix="%" />
            <MetricRow label="ROA" value={data.roa} suffix="%" />
            <MetricRow label="ROIC" value={data.roic} suffix="%" />
            <MetricRow label="Gross Margin" value={data.gross_margin} suffix="%" />
            <MetricRow label="Operating Margin" value={data.operating_margin} suffix="%" />
            <MetricRow label="Net Margin" value={data.net_margin} suffix="%" />
          </SectionCard>

          {/* Leverage & Credit */}
          <SectionCard title="Leverage & Credit" icon={Scale} badge={data.leverage_tier}>
            <div className="flex items-center gap-2 mb-4">
              <ClassificationTag label={data.leverage_tier} variant={data.leverage_tier === 'Conservative' ? 'success' : data.leverage_tier === 'Excessive' ? 'danger' : 'warning'} />
            </div>
            <MetricRow label="Credit Quality" value={data.credit_quality} />
            <MetricRow label="D/E Ratio" value={data.debt_to_equity} suffix="x" />
            <MetricRow label="Net Debt/EBITDA" value={data.net_debt_to_ebitda} suffix="x" />
            <MetricRow label="Interest Coverage" value={data.interest_coverage} suffix="x" />
            <MetricRow label="Leverage Score" value={data.leverage_score} suffix="/10" />
          </SectionCard>

          {/* Liquidity */}
          <SectionCard title="Liquidity" icon={Zap} badge={data.liquidity_tier}>
            <MetricRow label="Current Ratio" value={data.current_ratio} suffix="x" />
            <MetricRow label="Quick Ratio" value={data.quick_ratio} suffix="x" />
            <MetricRow label="Liquidity Score" value={data.liquidity_score} suffix="/10" />
          </SectionCard>

          {/* Cash Generation */}
          <SectionCard title="Cash Generation" icon={TrendingUp} badge={data.fcf_profile}>
            <MetricRow label="FCF Profile" value={data.fcf_profile} />
            <MetricRow label="FCF to Net Income" value={data.fcf_to_net_income} suffix="x" />
            <MetricRow label="FCF Margin" value={data.fcf_margin} suffix="%" />
          </SectionCard>

          {/* Competitive Position */}
          <SectionCard title="Competitive Position" icon={Shield}>
            <div className="flex flex-wrap gap-2 mb-4">
              <ClassificationTag
                label={data.competitive_moat}
                variant={data.competitive_moat.includes('Wide') ? 'success' : data.competitive_moat.includes('Narrow') ? 'warning' : 'danger'}
              />
            </div>
            <MetricRow label="Market Position" value={data.market_position} />
            <MetricRow label="Moat Sources" value={data.moat_sources.join(', ') || 'None'} />
            <MetricRow label="Pricing Power" value={data.pricing_power} />
            <MetricRow label="Margin Stability" value={data.gross_margin_stability} />
          </SectionCard>

          {/* Risk Profile */}
          <SectionCard title="Risk Profile" icon={Activity}>
            <div className="flex flex-wrap gap-2 mb-4">
              <ClassificationTag
                label={data.beta_classification}
                variant={data.beta_classification.includes('Low') ? 'success' : data.beta_classification.includes('High') ? 'danger' : 'warning'}
              />
              <ClassificationTag
                label={data.cyclical_profile}
                variant={data.cyclical_profile === 'Defensive' ? 'success' : data.cyclical_profile.includes('Highly') ? 'danger' : 'warning'}
              />
            </div>
            <MetricRow label="Beta" value={data.estimated_beta} />
            <MetricRow label="Volatility (Annual)" value={data.volatility_annual} suffix="%" />
            <MetricRow label="Cyclical Profile" value={data.cyclical_profile} />
            <MetricRow label="Economic Sensitivity" value={data.economic_sensitivity} />
          </SectionCard>

          {/* Business Model */}
          <SectionCard title="Business Model" icon={Briefcase}>
            <MetricRow label="Sector" value={data.gics_sector} />
            <MetricRow label="Industry" value={data.industry} />
            <MetricRow label="Asset Profile" value={data.asset_profile} />
            <MetricRow label="Revenue Model" value={data.revenue_model} />
            <MetricRow label="Customer Base" value={data.customer_base} />
            <MetricRow label="Platform Type" value={data.platform_type} />
          </SectionCard>

          {/* Ownership */}
          <SectionCard title="Ownership Signals" icon={Users}>
            <MetricRow label="Institutional %" value={data.institutional_ownership_pct} suffix="%" />
            <MetricRow label="Insider Activity" value={data.insider_activity} />
          </SectionCard>

          {/* Financial Health Summary */}
          <SectionCard title="Financial Health Summary" icon={Heart} className="lg:col-span-2 xl:col-span-1">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <GradeCard label="Health" grade={data.health_grade} description={data.overall_health} />
              <GradeCard label="Altman Z" grade={data.altman_z_zone.charAt(0)} description={data.altman_z_score?.toFixed(2) || 'N/A'} />
            </div>
            <MetricRow label="Piotroski F-Score" value={data.piotroski_f_score} suffix="/9" />
            <MetricRow label="Leverage Tier" value={data.leverage_tier} />
            <MetricRow label="Liquidity Tier" value={data.liquidity_tier} />
            <MetricRow label="Credit Quality" value={data.credit_quality} />
            <MetricRow label="FCF Profile" value={data.fcf_profile} />
          </SectionCard>

        </div>

        {/* All 60+ Fields Summary */}
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-gray-500" />
            Complete Classification Data (60+ Fields)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-1 text-sm">
            <MetricRow label="Symbol" value={data.symbol} />
            <MetricRow label="Company" value={data.company_name} />
            <MetricRow label="Market Cap" value={data.market_cap_formatted} />
            <MetricRow label="Cap Bucket" value={data.market_cap_bucket} />
            <MetricRow label="Sector Approach" value={data.sector_classification_approach} />
            <MetricRow label="Primary Style" value={data.primary_style} />
            <MetricRow label="Secondary Style" value={data.secondary_style} />
            <MetricRow label="Growth Score" value={data.growth_score} />
            <MetricRow label="Value Score" value={data.value_score} />
            <MetricRow label="Quality Score" value={data.quality_score} />
            <MetricRow label="Financial Score" value={data.financial_score} />
            <MetricRow label="Defensive Score" value={data.defensive_score} />
            <MetricRow label="Moat Score" value={data.moat_strength_score} />
            <MetricRow label="Leverage Tier" value={data.leverage_tier} />
            <MetricRow label="Leverage Score" value={data.leverage_score} />
            <MetricRow label="Credit Quality" value={data.credit_quality} />
            <MetricRow label="Liquidity Tier" value={data.liquidity_tier} />
            <MetricRow label="Liquidity Score" value={data.liquidity_score} />
            <MetricRow label="Current Ratio" value={data.current_ratio} />
            <MetricRow label="Quick Ratio" value={data.quick_ratio} />
            <MetricRow label="FCF Profile" value={data.fcf_profile} />
            <MetricRow label="FCF/Net Income" value={data.fcf_to_net_income} />
            <MetricRow label="FCF Margin" value={data.fcf_margin} suffix="%" />
            <MetricRow label="Health Grade" value={data.health_grade} />
            <MetricRow label="Overall Health" value={data.overall_health} />
            <MetricRow label="Altman Z" value={data.altman_z_score} />
            <MetricRow label="Altman Zone" value={data.altman_z_zone} />
            <MetricRow label="Piotroski F" value={data.piotroski_f_score} />
            <MetricRow label="Sector" value={data.gics_sector} />
            <MetricRow label="Industry" value={data.industry} />
            <MetricRow label="Asset Profile" value={data.asset_profile} />
            <MetricRow label="Revenue Model" value={data.revenue_model} />
            <MetricRow label="Customer Base" value={data.customer_base} />
            <MetricRow label="Platform Type" value={data.platform_type} />
            <MetricRow label="Moat" value={data.competitive_moat} />
            <MetricRow label="Market Position" value={data.market_position} />
            <MetricRow label="Pricing Power" value={data.pricing_power} />
            <MetricRow label="Margin Stability" value={data.gross_margin_stability} />
            <MetricRow label="Cyclical Profile" value={data.cyclical_profile} />
            <MetricRow label="Economic Sensitivity" value={data.economic_sensitivity} />
            <MetricRow label="Beta" value={data.estimated_beta} />
            <MetricRow label="Beta Class" value={data.beta_classification} />
            <MetricRow label="Volatility" value={data.volatility_annual} suffix="%" />
            <MetricRow label="P/E Actual" value={data.pe_ratio_actual} />
            <MetricRow label="P/B Actual" value={data.pb_ratio_actual} />
            <MetricRow label="P/S Actual" value={data.ps_ratio_actual} />
            <MetricRow label="EV/EBITDA" value={data.ev_ebitda_actual} />
            <MetricRow label="PEG Actual" value={data.peg_ratio_actual} />
            <MetricRow label="FCF Yield" value={data.fcf_yield} suffix="%" />
            <MetricRow label="Div Yield" value={data.dividend_yield} suffix="%" />
            <MetricRow label="P/E Bucket" value={data.pe_bucket} />
            <MetricRow label="P/B Bucket" value={data.pb_bucket} />
            <MetricRow label="EV/EBITDA Bucket" value={data.ev_ebitda_bucket} />
            <MetricRow label="PEG Bucket" value={data.peg_bucket} />
            <MetricRow label="Valuation Tier" value={data.valuation_tier} />
            <MetricRow label="Value Rating" value={data.value_rating} />
            <MetricRow label="Fair Value Gap" value={data.fair_value_gap} />
            <MetricRow label="Sector Relative" value={data.sector_relative_valuation} />
            <MetricRow label="Quality Rank" value={data.quality_rank} />
            <MetricRow label="ROE" value={data.roe} suffix="%" />
            <MetricRow label="ROA" value={data.roa} suffix="%" />
            <MetricRow label="ROIC" value={data.roic} suffix="%" />
            <MetricRow label="Gross Margin" value={data.gross_margin} suffix="%" />
            <MetricRow label="Op Margin" value={data.operating_margin} suffix="%" />
            <MetricRow label="Net Margin" value={data.net_margin} suffix="%" />
            <MetricRow label="D/E" value={data.debt_to_equity} />
            <MetricRow label="Net Debt/EBITDA" value={data.net_debt_to_ebitda} />
            <MetricRow label="Int Coverage" value={data.interest_coverage} />
            <MetricRow label="Inst. Own %" value={data.institutional_ownership_pct} suffix="%" />
            <MetricRow label="Insider Activity" value={data.insider_activity} />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          Generated: {new Date(data.generated_at).toLocaleString()} | Classification Approach: {data.sector_classification_approach}
        </div>
      </div>
    </div>
  );
}
